import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  ButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import API from "../services/API";
import { getEnvironmentAuthoringPayload } from "../services/Content";
import { useContent } from "../context/ContentContext";
import EnvironmentAudioUploader from "../components/environments/EnvironmentAudioUploader";
import WorldUnitEditor from "../components/WorldUnitEditor";
import { AppButton, AppTextField, Panel } from "../components/ui";
import "./EnvironmentsPage.scss";

const EnvironmentsPage = () => {
  const { myEnvironments, getItem } = useContent();
  const [environments, setEnvironments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editedEnvironments, setEditedEnvironments] = useState({});

  // States for sorting
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  // States for delete confirmation dialog
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [environmentToDelete, setEnvironmentToDelete] = useState(null);

  // map of elementId -> displayName
  const [elementNames, setElementNames] = useState({});

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Update local state when myEnvironments changes.
  useEffect(() => {
    if (myEnvironments) {
      setEnvironments(myEnvironments);
      setLoading(false);
    }
  }, [myEnvironments]);

  useEffect(() => {
    console.log("EDITED", editedEnvironments);
  }, [editedEnvironments]);

  // whenever we start editing or the elementIds change, fetch up any missing names
  useEffect(() => {
    if (!editingId) return;
    const ctx = editedEnvironments[editingId];
    if (!ctx || !ctx.elementIDs) return;

    ctx.elementIDs.forEach((id) => {
      if (!elementNames[id]) {
        console.log("ELEMENT ID", id);
        getItem("elements", id)
          .then((el) => {
            setElementNames((prev) => ({
              ...prev,
              [id]: el.data.labelData.displayName,
            }));
          })
          .catch(() => {
            setElementNames((prev) => ({ ...prev, [id]: "(not found)" }));
          });
      }
    });
  }, [editingId, editedEnvironments, getItem, elementNames]);

  // ----- Edit Existing Environment Handlers -----
  const handleRemoveElement = (envId, elementId) => {
    setEditedEnvironments((prev) => {
      const ctx = prev[envId];
      return {
        ...prev,
        [envId]: {
          ...ctx,
          elementIDs: ctx.elementIDs.filter((id) => id !== elementId),
        },
      };
    });
  };

  const handleWorldUnitChange = (envId, field, value) => {
    setEditedEnvironments((prev) => ({
      ...prev,
      [envId]: {
        ...prev[envId],
        worldUnit: {
          ...prev[envId].worldUnit,
          [field]: value,
        },
      },
    }));
  };

  const handleEdit = async (envId) => {
    try {
      const response = await API.get(`environments/${envId}`, {
        requiresAuth: true,
      });
      const fullEnv = response.data;
      console.log("FULL ENV", fullEnv);
      console.log("ENV ID", envId);

      setEditingId(envId);
      setEditedEnvironments((prev) => ({
        ...prev,
        [envId]: {
          name: fullEnv.environmentName || "",
          description: fullEnv.description || "",
          fullEnvironment: fullEnv,
          elementIDs: fullEnv.elementIDs || [],
          worldUnit: fullEnv.worldUnit || {},
        },
      }));
    } catch (error) {
      console.error("Error fetching full environment data:", error);
    }
  };

  const handleChange = (envId, field, value) => {
    setEditedEnvironments((prev) => ({
      ...prev,
      [envId]: {
        ...prev[envId],
        [field]: value,
      },
    }));
  };

  const handleCancel = () => {
    setEditingId(null);
  };

  const refreshBackgroundAudio = async (envId) => {
    const response = await API.get(`environments/${envId}`, {
      requiresAuth: true,
    });
    const refreshedEnvironment = response.data;

    setEditedEnvironments((prev) => ({
      ...prev,
      [envId]: {
        ...prev[envId],
        fullEnvironment: refreshedEnvironment,
      },
    }));

    return refreshedEnvironment?.backgroundAudioUrl || "";
  };

  const handleSave = async (envId) => {
    const currentData = editedEnvironments[envId];
    if (!currentData || !currentData.fullEnvironment) return;
    const updatedEnv = {
      ...getEnvironmentAuthoringPayload(currentData.fullEnvironment),
      environmentName: currentData.name,
      description: currentData.description,
      elementIDs: currentData.elementIDs,
      worldUnit: currentData.worldUnit,
    };

    try {
      await API.post("environments/", updatedEnv, { requiresAuth: true });
      setEnvironments((prev) =>
        prev.map((env) =>
          env.id === envId
            ? {
                ...env,
                name: updatedEnv.environmentName,
                description: updatedEnv.description,
              }
            : env
        )
      );
      setEditingId(null);
    } catch (error) {
      console.error("Error saving environment:", error);
    }
  };

  // ----- Delete Environment Handlers -----
  const handleDeleteClick = (env) => {
    setEnvironmentToDelete(env);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!environmentToDelete) return;
    try {
      await API.get(`environments/${environmentToDelete.id}/delete`, {
        requiresAuth: true,
      });
      setEnvironments((prev) => prev.filter((env) => env.id !== environmentToDelete.id));
    } catch (error) {
      console.error("Error deleting environment:", error);
    } finally {
      setDeleteConfirmOpen(false);
      setEnvironmentToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setEnvironmentToDelete(null);
  };

  // Sort environments before rendering.
  const sortedEnvironments = [...environments].sort((a, b) => {
    const aValue = a[sortField] || "";
    const bValue = b[sortField] || "";
    if (sortField === "name") {
      return sortOrder === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    } else if (sortField === "dateModified") {
      return sortOrder === "asc" ? new Date(aValue) - new Date(bValue) : new Date(bValue) - new Date(aValue);
    }
    return 0;
  });

  if (loading) {
    return (
      <div className="environmentsPage">
        <div className="environmentsLoading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="environmentsPage">
      <Typography className="environmentsTitle" variant="h4">
        Environments
      </Typography>

      {/* Sorting Controls */}
      <div className="environmentsSortRow">
        <div className="environmentsSortGroup">
          <ButtonGroup size="small" aria-label="sort environments">
            <AppButton
              kind={sortField === "name" ? "primary" : "secondary"}
              onClick={() => setSortField("name")}
            >
              Sort: Name
            </AppButton>
            <AppButton
              kind={sortField === "dateModified" ? "primary" : "secondary"}
              onClick={() => setSortField("dateModified")}
            >
              Sort: Date Updated
            </AppButton>
          </ButtonGroup>
        </div>

        <Tooltip title={sortOrder === "asc" ? "Ascending" : "Descending"}>
          <IconButton
            className="environmentsSortArrow"
            size="small"
            onClick={() => setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))}
          >
            {sortOrder === "asc" ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          </IconButton>
        </Tooltip>
      </div>

      {/* Environments List */}
      <div className="environmentsList app-row-list">
        {sortedEnvironments.map((env) => (
          <Panel key={env.id} className="environmentCard" hoverable>
            {editingId === env.id ? (
              <div className="environmentEditForm">
                <div className="environmentEditFields">
                  <AppTextField
                    label="Name"
                    value={editedEnvironments[env.id].name}
                    onChange={(e) => handleChange(env.id, "name", e.target.value)}
                    fullWidth
                  />
                  <AppTextField
                    label="Description"
                    value={editedEnvironments[env.id].description}
                    onChange={(e) => handleChange(env.id, "description", e.target.value)}
                    fullWidth
                  />
                </div>

                <div className="environmentEditActions">
                  <AppButton onClick={() => handleSave(env.id)}>
                    Save
                  </AppButton>
                  <AppButton kind="ghost" onClick={handleCancel}>
                    Cancel
                  </AppButton>
                </div>

                <div className="environmentEditSection">
                  <Typography className="environmentEditSectionTitle" variant="subtitle2">
                    Background Audio
                  </Typography>
                  <EnvironmentAudioUploader
                    environmentId={env.id}
                    audioUrl={
                      editedEnvironments[env.id]?.fullEnvironment?.backgroundAudioUrl
                    }
                    onUploaded={() => refreshBackgroundAudio(env.id)}
                  />
                </div>

                <div className="environmentEditSection">
                  <Typography className="environmentEditSectionTitle" variant="subtitle2">
                    World Unit
                  </Typography>
                  <Box sx={{ maxWidth: 400 }}>
                    <WorldUnitEditor
                      worldUnit={editedEnvironments[env.id]?.worldUnit}
                      onChange={(field, value) => handleWorldUnitChange(env.id, field, value)}
                    />
                  </Box>
                </div>

                <div className="environmentEditSection">
                  <Typography className="environmentEditSectionTitle" variant="subtitle2">
                    Elements
                  </Typography>
                  <div className="environmentElementsList">
                    {(editedEnvironments[env.id]?.elementIDs || []).map((elId) => (
                      <div key={elId} className="environmentElementItem">
                        <span className="environmentElementName">
                          {elementNames[elId] || "Loading..."} ({elId})
                        </span>
                        <AppButton
                          className="environmentElementBtn"
                          size="small"
                          kind="danger"
                          onClick={() => handleRemoveElement(env.id, elId)}
                        >
                          Remove
                        </AppButton>
                        <AppButton
                          className="environmentElementBtn"
                          size="small"
                          kind="ghost"
                          onClick={() => window.open(`elements/${elId}`, "_blank", "rel=noopener noreferrer")}
                        >
                          Open →
                        </AppButton>
                      </div>
                    ))}
                    {!editedEnvironments[env.id]?.elementIDs?.length && (
                      <Typography variant="caption" color="textSecondary">
                        (no elements)
                      </Typography>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="environmentCardContent">
                <div className="environmentCardInfo">
                  <div className="environmentCardName">{env.name || "No Name"}</div>
                  {env.status?.type && (
                    <div className="environmentCardStatus">{env.status?.type}</div>
                  )}
                </div>

                <div className="environmentCardDesc">
                  {env.description || "No Description"}
                </div>

                <div className="environmentCardActions">
                  <AppButton
                    className="environmentCardBtn"
                    kind="secondary"
                    onClick={() => handleEdit(env.id)}
                  >
                    Edit
                  </AppButton>
                  <AppButton
                    className="environmentCardBtn"
                    kind="danger"
                    onClick={() => handleDeleteClick(env)}
                  >
                    Delete
                  </AppButton>
                </div>
              </div>
            )}
          </Panel>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={cancelDelete}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{" "}
            {environmentToDelete ? environmentToDelete.name || "this environment" : "this environment"}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <AppButton kind="ghost" onClick={cancelDelete}>
            Cancel
          </AppButton>
          <AppButton kind="danger" onClick={confirmDelete}>
            Delete
          </AppButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EnvironmentsPage;
